import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import type { PresentationTemplate, PresentationTemplateInput } from '../types';

interface FormState {
  name: string;
  font_family: string;
  primary_color: string;
  secondary_color: string;
  minimum_font_size_px: string;
  safe_margin_percent: string;
  supports_dark_mode: boolean;
  is_default: boolean;
}

const EMPTY_FORM: FormState = {
  name: '', font_family: '', primary_color: '', secondary_color: '',
  minimum_font_size_px: '', safe_margin_percent: '', supports_dark_mode: true, is_default: false,
};

function toFormState(template: PresentationTemplate | null): FormState {
  if (!template) return EMPTY_FORM;
  return {
    name: template.name,
    font_family: template.font_family || '',
    primary_color: template.primary_color || '',
    secondary_color: template.secondary_color || '',
    minimum_font_size_px: template.minimum_font_size_px ? String(template.minimum_font_size_px) : '',
    safe_margin_percent: template.safe_margin_percent ? String(template.safe_margin_percent) : '',
    supports_dark_mode: template.supports_dark_mode,
    is_default: template.is_default,
  };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: PresentationTemplate | null;
  onSubmit: (data: PresentationTemplateInput) => Promise<void>;
}

export default function TemplateFormDialog({ open, onOpenChange, template, onSubmit }: Props) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm(toFormState(template));
  }, [open, template]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.name.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit({
        name: form.name.trim(),
        font_family: form.font_family || null,
        primary_color: form.primary_color || null,
        secondary_color: form.secondary_color || null,
        minimum_font_size_px: form.minimum_font_size_px ? Number(form.minimum_font_size_px) : null,
        safe_margin_percent: form.safe_margin_percent ? Number(form.safe_margin_percent) : null,
        supports_dark_mode: form.supports_dark_mode,
        is_default: form.is_default,
      });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{template ? 'Presentatiesjabloon bewerken' : 'Nieuw presentatiesjabloon'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="template-name">Naam</Label>
            <Input
              id="template-name"
              value={form.name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="template-font">Lettertype</Label>
            <Input
              id="template-font"
              value={form.font_family}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, font_family: e.target.value })}
              placeholder="Bijv. Inter"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="template-primary">Primaire kleur</Label>
              <Input
                id="template-primary" type="text" value={form.primary_color}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, primary_color: e.target.value })}
                placeholder="#0f766e"
              />
            </div>
            <div>
              <Label htmlFor="template-secondary">Secundaire kleur</Label>
              <Input
                id="template-secondary" type="text" value={form.secondary_color}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, secondary_color: e.target.value })}
                placeholder="#0f172a"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="template-font-size">Minimale tekstgrootte (px)</Label>
              <Input
                id="template-font-size" type="number" min={0} value={form.minimum_font_size_px}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, minimum_font_size_px: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="template-margin">Veilige marge (%)</Label>
              <Input
                id="template-margin" type="number" min={0} max={50} value={form.safe_margin_percent}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, safe_margin_percent: e.target.value })}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="template-dark-mode"
              checked={form.supports_dark_mode}
              onCheckedChange={(checked: boolean) => setForm({ ...form, supports_dark_mode: checked })}
            />
            <Label htmlFor="template-dark-mode" className="font-normal">Ondersteunt donkere weergave</Label>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              id="template-default"
              checked={form.is_default}
              onCheckedChange={(checked: boolean) => setForm({ ...form, is_default: checked })}
            />
            <Label htmlFor="template-default" className="font-normal">Standaardsjabloon voor deze organisatie</Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
            <Button type="submit" disabled={submitting || !form.name.trim()}>
              {template ? 'Opslaan' : 'Aanmaken'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
